// client/src/flow/FlowViewer.js
import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Phone, Mail, AlertCircle, Home, Download, Copy } from 'lucide-react';
import config from './config';
import FormScreen from './components/FormScreen';
import './FlowViewer.css';
import './themes/capelania.css';
import { applyCapelaniaContentRules } from './themes/capelaniaContent';
import UgitsLogo from './components/UgitsLogo';

// Importar o hook para processar o flow
import { useProcessedFlow } from './components/NavigationHandler';

const FlowViewer = () => {
  const { flowUrl } = useParams();
  const [rawFlow, setRawFlow] = useState(null); // Flow original do servidor
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [formData, setFormData] = useState({});
  const [protocol, setProtocol] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  
  // Estados para navegação em árvore
  const [navigationHistory, setNavigationHistory] = useState([]);
  const [navigationStep, setNavigationStep] = useState(null);
  
  // Processar o flow para converter URLs do WhatsApp
  const flow = useProcessedFlow(rawFlow);
  
  useEffect(() => {
    loadFlow();
  }, [flowUrl]);
  
  // Efeito para scroll ao topo quando step muda
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [navigationStep, currentStepIndex]);
  
  const loadFlow = async () => {
    try {
      const response = await fetch(`${config.serverUrl}/api/flows/view/${flowUrl}`);
      if (!response.ok) {
        if (response.status === 404) {
          setError('Página não encontrada');
        } else {
          setError('Erro ao carregar página');
        }
        setLoading(false);
        return;
      }
      
      const payload = await response.json();
      // Renomeação institucional EBSERH -> HU Brasil (só nesta página)
      const data = flowUrl === 'capelania' ? applyCapelaniaContentRules(payload) : payload;

      setRawFlow(data); // Salvar como rawFlow ao invés de flow
      document.title = data.name || 'Link2.do';
      
      // Aplicar tema completo
      if (data.theme) {
        const root = document.documentElement;
        const theme = data.theme;
        
        // Cores principais
        if (theme.primaryColor) {
          root.style.setProperty('--primary-color', theme.primaryColor);
        }
        if (theme.primaryColorHover) {
          root.style.setProperty('--primary-color-hover', theme.primaryColorHover);
        } else if (theme.primaryColor) {
          // Calcular hover automaticamente se não definido
          root.style.setProperty('--primary-color-hover', calculateHoverColor(theme.primaryColor));
        }
        
        if (theme.secondaryColor) {
          root.style.setProperty('--secondary-color', theme.secondaryColor);
        }
        if (theme.secondaryColorHover) {
          root.style.setProperty('--secondary-color-hover', theme.secondaryColorHover);
        } else if (theme.secondaryColor) {
          root.style.setProperty('--secondary-color-hover', calculateHoverColor(theme.secondaryColor));
        }
        
        // Backgrounds
        if (theme.pageBackground) {
          root.style.setProperty('--page-background', theme.pageBackground);
        }
        if (theme.cardBackground) {
          root.style.setProperty('--card-background', theme.cardBackground);
        }
        if (theme.inputBackground) {
          root.style.setProperty('--input-background', theme.inputBackground);
        }
        
        // Textos
        if (theme.textPrimary) {
          root.style.setProperty('--text-primary', theme.textPrimary);
        }
        if (theme.textSecondary) {
          root.style.setProperty('--text-secondary', theme.textSecondary);
        }
        if (theme.textOnPrimary) {
          root.style.setProperty('--text-on-primary', theme.textOnPrimary);
        }
        if (theme.textOnSecondary) {
          root.style.setProperty('--text-on-secondary', theme.textOnSecondary);
        }
        
        // Bordas
        if (theme.borderColor) {
          root.style.setProperty('--border-color', theme.borderColor);
        }
        if (theme.borderRadius) {
          root.style.setProperty('--border-radius', theme.borderRadius);
        }
        
        // Estados
        if (theme.errorColor) {
          root.style.setProperty('--error-color', theme.errorColor);
        }
        if (theme.successColor) {
          root.style.setProperty('--success-color', theme.successColor);
        }
        if (theme.warningColor) {
          root.style.setProperty('--warning-color', theme.warningColor);
        }
        if (theme.infoColor) {
          root.style.setProperty('--info-color', theme.infoColor);
        }
        
        // Fontes
        if (theme.fontFamily) {
          root.style.setProperty('--font-family', theme.fontFamily);
        }
        if (theme.fontSize) {
          root.style.setProperty('--font-size', theme.fontSize);
        }
        if (theme.headingFontFamily) {
          root.style.setProperty('--heading-font-family', theme.headingFontFamily);
        }
        if (theme.headingFontWeight) {
          root.style.setProperty('--heading-font-weight', theme.headingFontWeight);
        }
        
        // Botões
        if (theme.buttonPadding) {
          root.style.setProperty('--button-padding', theme.buttonPadding);
        }
        if (theme.buttonFontWeight) {
          root.style.setProperty('--button-font-weight', theme.buttonFontWeight);
        }
        
        // Sombras
        if (theme.boxShadow) {
          root.style.setProperty('--shadow-sm', theme.boxShadow);
          root.style.setProperty('--box-shadow', theme.boxShadow);
        }
        if (theme.boxShadowHover) {
          root.style.setProperty('--shadow-md', theme.boxShadowHover);
        }
        
        // Animações
        if (theme.transitionDuration) {
          root.style.setProperty('--transition-duration', theme.transitionDuration);
        }
        
        // Progress Bar
        if (theme.progressBarColor) {
          root.style.setProperty('--progress-bar-color', theme.progressBarColor);
        }
        if (theme.progressBarBackground) {
          root.style.setProperty('--progress-bar-background', theme.progressBarBackground);
        }
        
        // Logo
        if (theme.logoBackground) {
          root.style.setProperty('--logo-background', theme.logoBackground);
        }
        if (theme.logoPadding) {
          root.style.setProperty('--logo-padding', theme.logoPadding);
        }
        if (theme.logoBorderRadius) {
          root.style.setProperty('--logo-border-radius', theme.logoBorderRadius);
        }
        
        // Background light (calculado)
        if (theme.cardBackground) {
          const bgLight = adjustBrightness(theme.cardBackground, -5);
          root.style.setProperty('--background-light', bgLight);
        }
        
        // Aplicar variáveis personalizadas para Capelania
        if (theme.alertInfoBackground) {
          root.style.setProperty('--alert-info-background', theme.alertInfoBackground);
        }
        if (theme.alertInfoBorder) {
          root.style.setProperty('--alert-info-border', theme.alertInfoBorder);
        }
        if (theme.footerBackground) {
          root.style.setProperty('--footer-background', theme.footerBackground);
        }
        if (theme.footerBorderColor) {
          root.style.setProperty('--footer-border-color', theme.footerBorderColor);
        }
        
        // Ajustar estilo do botão baseado no buttonStyle
        if (theme.buttonStyle) {
          switch (theme.buttonStyle) {
            case 'square':
              root.style.setProperty('--border-radius', '0px');
              break;
            case 'pill':
              root.style.setProperty('--border-radius', '50px');
              break;
            // 'rounded' já é o padrão
          }
        }
        
        // Ajustes específicos para modo tree
        if (data.formConfig?.settings?.navigationMode === 'tree') {
          root.style.setProperty('--progress-bar-height', '0px');
        }
      }
      
      // Garantir scroll no topo após carregar
      setTimeout(() => window.scrollTo(0, 0), 100);
      
      // Definir step inicial para modo tree
      if (data.formConfig?.settings?.navigationMode === 'tree') {
        setNavigationStep('home');
      }
    } catch (error) {
      console.error('Erro ao carregar flow:', error);
      setError('Erro ao conectar com o servidor');
    } finally {
      setLoading(false);
    }
  };
  
  // Helper function para calcular cor hover
  const calculateHoverColor = (color, amount = -20) => {
    // Remove # se existir
    color = color.replace('#', '');
    
    // Converte para RGB
    const r = parseInt(color.substring(0, 2), 16);
    const g = parseInt(color.substring(2, 4), 16);
    const b = parseInt(color.substring(4, 6), 16);
    
    // Ajusta brilho
    const newR = Math.max(0, Math.min(255, r + amount));
    const newG = Math.max(0, Math.min(255, g + amount));
    const newB = Math.max(0, Math.min(255, b + amount));
    
    // Converte de volta para hex
    return `#${((1 << 24) + (newR << 16) + (newG << 8) + newB).toString(16).slice(1)}`;
  };
  
  // Helper function para ajustar brilho
  const adjustBrightness = (color, amount) => {
    return calculateHoverColor(color, amount);
  };
  
  const updateFormData = (data) => {
    setFormData(prev => ({ ...prev, ...data }));
  };
  
  // Função para navegação em árvore
  const handleNavigate = (targetStepId) => {
    // Usar flow processado
    const targetStep = flow?.formConfig?.steps?.find(s => s.id === targetStepId);
    if (!targetStep) return;
    
    // Se voltando ao início, limpar histórico
    if (targetStepId === 'home') {
      setNavigationHistory([]);
      setNavigationStep('home');
    } else {
      // Adicionar ao histórico apenas se não estiver voltando
      const currentStepId = navigationStep || flow?.formConfig?.steps?.[currentStepIndex]?.id;
      if (currentStepId && !navigationHistory.includes(currentStepId)) {
        setNavigationHistory([...navigationHistory, currentStepId]);
      }
      setNavigationStep(targetStepId);
    }
    
    // Garantir scroll no topo
    setTimeout(() => window.scrollTo(0, 0), 0);
  };
  
  // Função para voltar na navegação
  const handleNavigateBack = (toIndex = null) => {
    if (toIndex !== null && toIndex >= 0 && toIndex < navigationHistory.length) {
      // Voltar para um índice específico no histórico
      const targetStep = navigationHistory[toIndex];
      const newHistory = navigationHistory.slice(0, toIndex);
      setNavigationHistory(newHistory);
      setNavigationStep(targetStep);
    } else if (navigationHistory.length > 0) {
      // Voltar um passo
      const newHistory = [...navigationHistory];
      const previousStep = newHistory.pop();
      setNavigationHistory(newHistory);
      setNavigationStep(previousStep || 'home');
    } else {
      // Se não houver histórico, voltar ao início
      setNavigationStep('home');
    }
    
    // Garantir scroll no topo
    setTimeout(() => window.scrollTo(0, 0), 0);
  };
  
  const nextStep = () => {
    const steps = flow?.formConfig?.steps || [];
    if (currentStepIndex < steps.length - 1) {
      setCurrentStepIndex(currentStepIndex + 1);
      window.scrollTo(0, 0);
    }
  };
  
  const prevStep = () => {
    if (currentStepIndex > 0) {
      setCurrentStepIndex(currentStepIndex - 1);
      window.scrollTo(0, 0);
    }
  };
  
  const submitForm = async () => {
    setSubmitting(true);
    try {
      const response = await fetch(`${config.serverUrl}/api/flows/${flowUrl}/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...formData, submittedAt: new Date().toISOString() })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setProtocol(data.protocol);
        setSubmitted(true);
        
        // Enviar email se configurado
        if (flow?.formConfig?.settings?.sendEmail && flow?.formConfig?.email) {
          // O backend deve lidar com o envio de email
          console.log('Email seria enviado para:', flow.formConfig.email);
        }
        
        // Notificar solicitante se tiver dados
        if (formData.emailSolicitante || formData.whatsappSolicitante) {
          console.log('Notificação seria enviada ao solicitante');
        }
      } else {
        alert('Erro ao enviar solicitação. Tente novamente.');
      }
    } catch (error) {
      console.error('Erro ao enviar:', error);
      alert('Erro ao enviar solicitação. Verifique sua conexão.');
    } finally {
      setSubmitting(false);
    }
  };
  
  const resetForm = () => {
    setCurrentStepIndex(0);
    setFormData({});
    setProtocol('');
    setSubmitted(false);
    setNavigationHistory([]);
    setNavigationStep(null);
    window.scrollTo(0, 0);
  };
  
  const downloadPDF = () => {
    // Gerar PDF com os dados do formulário
    const pdfContent = `
COMPROVANTE DE SOLICITAÇÃO
========================
Protocolo: ${protocol}
Data: ${new Date().toLocaleString('pt-BR')}

${flow?.name}

DADOS DA SOLICITAÇÃO:
${JSON.stringify(formData, null, 2)}

========================
${flow?.formConfig?.urgentPhone ? `Telefone: ${flow.formConfig.urgentPhone}` : ''}
${flow?.formConfig?.email ? `Email: ${flow.formConfig.email}` : ''}
    `;
    
    const blob = new Blob([pdfContent], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `protocolo-${protocol}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };
  
  const copyProtocol = async () => {
    try {
      await navigator.clipboard.writeText(protocol);
      alert('Protocolo copiado!');
    } catch {
      alert(`Protocolo: ${protocol}`);
    }
  };
  
  // Loading
  if (loading) {
    return (
      <div className="flow-container loading">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Carregando...</p>
        </div>
      </div>
    );
  }
  
  // Error
  if (error || !flow) {
    return (
      <div className="flow-container error">
        <div className="error-card">
          <AlertCircle size={64} />
          <h2>{error || 'Erro ao carregar formulário'}</h2>
          <p>A página que você está procurando não existe ou foi removida.</p>
          <button onClick={() => window.location.href = '/'} className="btn btn-primary">
            <Home size={20} />
            Voltar ao início
          </button>
        </div>
      </div>
    );
  }
  
  // Verificar se tem steps
  const steps = flow?.formConfig?.steps || [];
  if (steps.length === 0) {
    return (
      <div className="flow-container empty">
        <div className="empty-card">
          <AlertCircle size={64} />
          <h2>Formulário em construção</h2>
          <p>Este formulário ainda não possui etapas configuradas.</p>
        </div>
      </div>
    );
  }
  
  // Determinar o step atual
  // Se estiver em modo navegação, usar navigationStep
  // Senão, usar o índice tradicional
  const currentStep =
    (navigationStep
      ? flow.formConfig.steps.find(s => s.id === navigationStep)
      : flow.formConfig.steps[currentStepIndex]) || steps[0];
  
  const isFirstStep = currentStepIndex === 0 && !navigationHistory.length;
  const isLastStep = currentStepIndex === steps.length - 1 && !navigationStep;
  const progress = ((currentStepIndex + 1) / steps.length) * 100;
  
  // Classe condicional para modo tree
  const viewerClass = flow.formConfig.settings?.navigationMode === 'tree' ? 'mode-tree' : '';

  // Classe por fluxo (ex.: flow-capelania) para o tema visual daquela página.
  const flowClass = flow?.url ? `flow-${flow.url}` : '';
  
  // Render form
  return (
    <div className={`flow-container ${viewerClass} ${flowClass}`}>
      {/* Progress Bar - mostrar apenas para fluxos lineares */}
      {flow.formConfig.settings?.showProgressBar !== false && flow.formConfig.settings?.navigationMode !== 'tree' && (
        <div className="progress-bar" role="progressbar" aria-valuenow={Math.round(progress)} aria-valuemin={0} aria-valuemax={100}>
          <div className="progress-fill" style={{ width: `${progress}%` }}></div>
        </div>
      )}
      
      {/* Main Content */}
      <div className="flow-content">
        <FormScreen
          step={currentStep?.id}
          formData={formData}
          updateFormData={updateFormData}
          flow={flow} // Passar o flow processado
          protocol={protocol}
          onNext={nextStep}
          onPrev={prevStep}
          onSubmit={submitForm}
          onReset={resetForm}
          onDownloadPDF={downloadPDF}
          onCopyProtocol={copyProtocol}
          onNavigate={handleNavigate}
          submitting={submitting}
          submitted={submitted}
          isFirstStep={isFirstStep}
          isLastStep={isLastStep}
          currentStepIndex={currentStepIndex}
          totalSteps={steps.length}
          logo={flow?.url === 'ugits' ? null : flow?.logo}
          navigationHistory={navigationHistory}
          onNavigateBack={handleNavigateBack}
        />
      </div>
      
      {/* Footer */}
      {!submitted && (flow?.formConfig?.urgentPhone || flow?.formConfig?.email) && (
        <div className="flow-footer">
          <div className="contact-info">
            {flow.formConfig.urgentPhone && (
              <a href={`tel:${flow.formConfig.urgentPhone}`} className="contact-item">
                <Phone size={16} />
                Urgência: {flow.formConfig.urgentPhone}
              </a>
            )}
            {flow.formConfig.email && (
              <a href={`mailto:${flow.formConfig.email}`} className="contact-item">
                <Mail size={16} />
                {flow.formConfig.email}
              </a>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default FlowViewer;